import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Input } from '../../../../shared';
import { TEXT, PLACEHOLDER } from '../../../../../constants';
import { dateFormatChanger, showDate } from '../../../../../utils';

/**
 * SewingFieldsGroup - Reusable component for sewing-related form fields
 * Handles both create mode (always editable) and edit mode (editable or readonly)
 * Memoized to prevent unnecessary re-renders when props haven't changed
 *
 * @param {string} mode - 'create' | 'edit' | 'readonly'
 * @param {Object} values - { sewQty, sewDmgQty, sewDate }
 * @param {Function} onSewQtyChange - Handler for sew qty change
 * @param {Function} onSewDmgQtyChange - Handler for sew damage qty change
 * @param {Function} onSewDateChange - Handler for sew date change
 * @param {Object} refs - { sewQuantity, sewDmgQuantity }
 * @param {number} availableCapacity - Available capacity for sewing (for placeholder)
 * @param {Object} batchData - Existing batch data (for edit/readonly modes)
 * @param {string} minDate - Minimum date allowed (for date input)
 * @param {boolean} isEditable - Whether fields are editable (for edit mode)
 */
const SewingFieldsGroupComponent = ({
  mode = 'create',
  values,
  onSewQtyChange,
  onSewDmgQtyChange,
  onSewDateChange,
  refs,
  availableCapacity,
  batchData,
  minDate,
  isEditable = true
}) => {
  const isCreateMode = mode === 'create';
  const isEditMode = mode === 'edit';
  // const isReadonlyMode = mode === 'readonly';

  /**
   * Determine whether to show input fields or readonly display
   * Business Rules:
   * - Create mode: Always editable (creating new sewing report)
   * - Edit mode: Editable only if isEditable=true AND field not yet filled
   * - Readonly mode: Always display values
   *
   * This prevents accidental overwrites of existing sewing data
   */
  const shouldShowInput = isCreateMode || (isEditMode && isEditable);

  return (
    <>
      {/* Sew Quantity */}
      <div className="col-4">{TEXT.SEW_QTY}</div>
      {shouldShowInput ? (
        <Input
          ref={refs?.sewQuantity}
          type="number"
          value={values.sewQty}
          className="col-8"
          placeholder={isCreateMode ? `Available: ${availableCapacity}` : availableCapacity}
          /**
           * Disable if in edit mode and sew_qty already exists
           * Business Rule: Once sewing is complete, the sew quantity cannot be changed
           * This maintains data integrity and prevents accidental modifications
           */
          disabled={isEditMode && batchData?.sew_qty}
          onChange={(e) => {
            // Let the hook handle validation to avoid double validation
            const inputValue = e.target.value === '' ? '' : +e.target.value;
            onSewQtyChange(inputValue);
          }}
          // Prevent mouse wheel from changing number input
          onWheel={(e) => e.target.blur()}
          min={1}
        />
      ) : (
        <div className="col-8">{batchData?.sew_qty || 0}</div>
      )}

      {/* Damaged Sew Quantity */}
      <div className="col-4">Damaged Sew Quantity</div>
      {shouldShowInput ? (
        <Input
          ref={refs?.sewDmgQuantity}
          type="number"
          value={values.sewDmgQty}
          className="col-8"
          onChange={(e) => {
            // Let the hook handle validation to avoid double validation
            const inputValue = e.target.value === '' ? '' : +e.target.value;
            onSewDmgQtyChange(inputValue);
          }}
          onWheel={(e) => e.target.blur()}
        />
      ) : (
        <div className="col-8">{batchData?.sew_damage_qty || 0}</div>
      )}

      {/* Sew Date */}
      <div className="col-4">{TEXT.SEW_DATE}</div>
      {shouldShowInput ? (
        <Input
          type="date"
          className="col-8"
          placeholder={PLACEHOLDER.ENTER_DATE}
          value={showDate(values.sewDate)}
          onChange={(e) => onSewDateChange(e.target.value)}
          min={minDate}
          max={showDate()}
          onKeyDown={(e) => e.preventDefault()}
        />
      ) : (
        <div className="col-8">{dateFormatChanger(batchData?.sew_date)}</div>
      )}
    </>
  );
};

SewingFieldsGroupComponent.propTypes = {
  mode: PropTypes.oneOf(['create', 'edit', 'readonly']),
  values: PropTypes.shape({
    sewQty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    sewDmgQty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    // Backend sometimes returns false/null for dates instead of strings
    sewDate: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired
  }).isRequired,
  onSewQtyChange: PropTypes.func.isRequired,
  onSewDmgQtyChange: PropTypes.func.isRequired,
  onSewDateChange: PropTypes.func.isRequired,
  refs: PropTypes.shape({
    sewQuantity: PropTypes.object,
    sewDmgQuantity: PropTypes.object
  }),
  availableCapacity: PropTypes.number,
  batchData: PropTypes.object,
  minDate: PropTypes.string,
  isEditable: PropTypes.bool
};

export const SewingFieldsGroup = memo(SewingFieldsGroupComponent);
